/**
 * useVoiceRecorder — Task 3.9
 * Custom hook for MediaRecorder-based audio capture.
 * Isolates all browser API complexity out of the calling component.
 *
 * Usage:
 *   const { isRecording, startRecording, stopRecording } = useVoiceRecorder({
 *     onBlob:  (blob, mimeType) => { ... },  // called when recording stops with audio data
 *     onError: (message) => { ... }          // called on permission denied or unavailable
 *   });
 *
 * MIME type: audio/webm if supported (Chrome), audio/mp4 fallback (Safari).
 * Stream tracks are stopped on recording end — microphone indicator light turns off.
 * Permission denied → onError callback, never a thrown error.
 */

import { useRef, useState, useCallback } from 'react';

function getSupportedMimeType() {
  if (typeof MediaRecorder === 'undefined') return '';
  if (MediaRecorder.isTypeSupported('audio/webm')) return 'audio/webm';
  if (MediaRecorder.isTypeSupported('audio/mp4'))  return 'audio/mp4';
  return '';
}

export function useVoiceRecorder({ onBlob, onError }) {
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef              = useRef(null);
  const chunksRef                     = useRef([]);
  const streamRef                     = useRef(null);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = getSupportedMimeType();
      const options  = mimeType ? { mimeType } : {};
      const recorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        // setIsRecording(false) here (not in stopRecording) so UI stays "recording"
        // until the blob is ready — prevents double-tap before audio data is available
        setIsRecording(false);
        // Stop all stream tracks — turns off the microphone indicator light in the browser
        stream.getTracks().forEach(track => track.stop());
        streamRef.current = null;

        const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' });
        chunksRef.current = [];
        if (onBlob) onBlob(blob, mimeType || 'audio/webm');
      };

      recorder.start();
      setIsRecording(true);
    } catch (err) {
      // Never throw — always surface via onError so the UI can show an inline message
      const message = err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError'
        ? 'Microphone access denied. Please allow microphone permission and try again.'
        : 'Microphone unavailable. Please check your device settings.';
      if (onError) onError(message);
    }
  }, [onBlob, onError]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      // setIsRecording(false) is intentionally in recorder.onstop, not here —
      // onstop fires asynchronously after stop(); calling it here would
      // clear the "recording" state before the blob chunk is assembled.
    }
  }, []);

  return { isRecording, startRecording, stopRecording };
}
